<template>
  <div class="min-h-screen bg-gradient-to-br from-amber-50 to-yellow-50 p-4 md:p-6">
    <!-- Header -->
    <div class="max-w-6xl mx-auto">
      <header class="flex flex-col md:flex-row justify-between items-center mb-8 md:mb-10">
        <div>
          <h1 class="text-3xl md:text-4xl font-bold text-amber-900">Gold Price Tracker</h1>
          <p class="text-amber-700 mt-2">Live gold prices from BOC (Bank of China)</p>
        </div>
        <div class="mt-4 md:mt-0 flex items-center space-x-4">
          <div class="flex items-center bg-white rounded-full px-4 py-2 shadow-sm">
            <div class="w-3 h-3 rounded-full bg-green-500 animate-pulse mr-2"></div>
            <span class="text-sm font-medium text-gray-700">Live</span>
          </div>
          <div class="text-sm text-gray-600">
            Updates every 3 minutes
          </div>
        </div>
      </header>

      <!-- Main Content -->
      <div class="bg-white rounded-2xl shadow-xl overflow-hidden">
        <!-- Last Updated Info -->
        <div class="bg-gradient-to-r from-amber-500 to-yellow-500 px-6 py-4 flex justify-between items-center">
          <div class="text-white">
            <div class="text-sm font-medium">Last Updated</div>
            <div class="text-lg font-semibold">{{ lastUpdated }}</div>
          </div>
          <div class="text-white text-right">
            <div class="text-sm font-medium">Next Update In</div>
            <div class="text-lg font-semibold">{{ formatTimeUntilNextUpdate }}</div>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="p-8 text-center">
          <div class="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500 mb-4"></div>
          <p class="text-gray-600">Fetching gold prices...</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="p-8 text-center">
          <div class="text-red-500 text-5xl mb-4">⚠️</div>
          <h3 class="text-xl font-semibold text-gray-800 mb-2">Unable to Load Data</h3>
          <p class="text-gray-600 mb-4">{{ error }}</p>
          <button
              @click="fetchGoldPrice"
              class="bg-amber-500 hover:bg-amber-600 text-white px-6 py-2 rounded-lg transition-colors"
          >
            Try Again
          </button>
        </div>

        <!-- Data Display -->
        <div v-else-if="goldPrice" class="p-6">
          <!-- Current Price Display -->
          <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div class="bg-gradient-to-br from-amber-50 to-yellow-50 p-6 rounded-xl border border-amber-200">
              <div class="text-amber-700 font-medium mb-2">Buy Price</div>
              <div class="text-3xl font-bold text-amber-900">
                ¥{{ formatPrice(goldPrice.cashBuyPrc) }}
              </div>
              <div class="text-sm text-amber-600 mt-2">Per gram</div>
            </div>

            <div class="bg-gradient-to-br from-yellow-50 to-amber-50 p-6 rounded-xl border border-yellow-200">
              <div class="text-amber-700 font-medium mb-2">Sell Price</div>
              <div class="text-3xl font-bold text-amber-900">
                ¥{{ formatPrice(goldPrice.cashSalPrc) }}
              </div>
              <div class="text-sm text-amber-600 mt-2">Per gram</div>
            </div>

            <div class="bg-gradient-to-br from-gray-50 to-gray-100 p-6 rounded-xl border border-gray-200">
              <div class="text-gray-700 font-medium mb-2">Spread</div>
              <div class="text-3xl font-bold text-gray-800">
                ¥{{ formatPrice(goldPrice.cashBuyPrc - goldPrice.cashSalPrc) }}
              </div>
              <div class="text-sm text-gray-600 mt-2">Buy-Sell Difference</div>
            </div>
          </div>

          <!-- Detailed Information - Now using simple divs instead of InfoCard component -->
          <div class="bg-gray-50 rounded-xl p-6">
            <h3 class="text-lg font-semibold text-gray-800 mb-4">Detailed Information</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <!-- Currency Pair Card -->
              <div class="bg-white p-4 rounded-lg border border-gray-200">
                <div class="flex items-center mb-2">
                  <span class="text-xl mr-2">💰</span>
                  <div class="text-sm font-medium text-gray-500">Currency Pair</div>
                </div>
                <div class="text-lg font-semibold text-gray-800 truncate">{{ goldPrice.rateCode || 'N/A' }}</div>
              </div>

              <!-- Base Currency Card -->
              <div class="bg-white p-4 rounded-lg border border-gray-200">
                <div class="flex items-center mb-2">
                  <span class="text-xl mr-2">💵</span>
                  <div class="text-sm font-medium text-gray-500">Base Currency</div>
                </div>
                <div class="text-lg font-semibold text-gray-800 truncate">{{ goldPrice.baseCur || 'N/A' }}</div>
              </div>

              <!-- Quote Currency Card -->
              <div class="bg-white p-4 rounded-lg border border-gray-200">
                <div class="flex items-center mb-2">
                  <span class="text-xl mr-2">💱</span>
                  <div class="text-sm font-medium text-gray-500">Quote Currency</div>
                </div>
                <div class="text-lg font-semibold text-gray-800 truncate">{{ goldPrice.quoteCur || 'N/A' }}</div>
              </div>

              <!-- Update Time Card -->
              <div class="bg-white p-4 rounded-lg border border-gray-200">
                <div class="flex items-center mb-2">
                  <span class="text-xl mr-2">⏰</span>
                  <div class="text-sm font-medium text-gray-500">Update Time</div>
                </div>
                <div class="text-lg font-semibold text-gray-800 truncate">{{ formatDateTime(goldPrice.priceTime) || 'N/A' }}</div>
              </div>
            </div>
          </div>

          <!-- Price History Chart (Mock) -->
          <div class="mt-8">
            <h3 class="text-lg font-semibold text-gray-800 mb-4">Price Trend (Last 24 Hours)</h3>
            <div class="h-48 bg-gradient-to-t from-amber-50 to-transparent rounded-lg border border-amber-200 flex items-center justify-center">
              <div class="text-gray-500 text-center">
                <div class="text-3xl mb-2">📈</div>
                <p>Chart visualization would appear here</p>
                <p class="text-sm mt-1">Based on historical price data</p>
              </div>
            </div>
          </div>
        </div>

        <!-- No Data State -->
        <div v-else class="p-8 text-center">
          <div class="text-amber-500 text-5xl mb-4">🔍</div>
          <h3 class="text-xl font-semibold text-gray-800 mb-2">No Data Available</h3>
          <p class="text-gray-600">Gold price information will appear here once loaded.</p>
        </div>

        <!-- Refresh Controls -->
        <div class="border-t border-gray-200 px-6 py-4 bg-gray-50">
          <div class="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
            <div class="text-sm text-gray-600">
              Data refreshes automatically every 3 minutes
            </div>
            <div class="flex space-x-3">
              <button
                  @click="fetchGoldPrice"
                  :disabled="loading"
                  class="flex items-center bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg v-if="loading" class="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <svg v-else class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                </svg>
                {{ loading ? 'Refreshing...' : 'Refresh Now' }}
              </button>
              <button
                  @click="toggleAutoRefresh"
                  class="flex items-center border border-gray-300 hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-lg transition-colors"
              >
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                {{ autoRefresh ? 'Disable Auto-Refresh' : 'Enable Auto-Refresh' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <footer class="mt-8 text-center text-gray-600 text-sm">
        <p>Gold prices are updated from Bank of China. Prices are for reference only.</p>
        <p class="mt-1">Last API call: {{ lastApiCallTime }}</p>
      </footer>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, computed, watch } from 'vue'
import axios from 'axios'

export default {
  name: 'GoldPriceTracker',
  setup() {
    // State
    const goldPrice = ref(null)
    const loading = ref(false)
    const error = ref(null)
    const lastUpdated = ref('')
    const lastApiCallTime = ref('')
    const autoRefresh = ref(true)
    let refreshInterval = null
    const nextUpdateTime = ref(Date.now() + 3 * 60 * 1000)

    // Headers for the API
    const getHeaders = () => {
      const now = new Date()
      const trandt = now.toISOString().slice(0, 10).replace(/-/g, '')
      const trantm = now.toTimeString().slice(0, 8).replace(/:/g, '')

      return {
        'Accept': 'application/json, text/plain, */*',
        'Accept-Language': 'en-US,en;q=0.9',
        'Cache-Control': 'no-cache',
        'Connection': 'keep-alive',
        'Content-Type': 'application/json;charset=UTF-8',
        'Origin': 'https://openapi.boc.cn',
        'Pragma': 'no-cache',
        'Referer': 'https://openapi.boc.cn/erh/jljBank/EBank.html',
        'Sec-Fetch-Dest': 'empty',
        'Sec-Fetch-Mode': 'cors',
        'Sec-Fetch-Site': 'same-origin',
        'User-Agent': 'Mozilla/5.0 (Linux; Android 6.0; Nexus 5 Build/MRA58N) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/143.0.0.0 Mobile Safari/537.36',
        'acton': '',
        'authTellerNo': '9881000',
        'bankNo': '003',
        'branchNo': '00323',
        'chnflg': '6',
        'clentid': '540',
        'clientid': '540',
        'sec-ch-ua': '"Google Chrome";v="143", "Chromium";v="143", "Not-A.Brand";v="24"',
        'sec-ch-ua-mobile': '?1',
        'sec-ch-ua-platform': '"Android"',
        'tellerNo': '9881000',
        'timeout': '100000',
        'trandt': trandt,
        'transNo': 'Q00001',
        'trantm': trantm,
        'userid': '',
        'uuid': 'z7zewdyFXdduB1DCgbSS6y4zSMowqAgw',
        // 'uuid': generateUUID()
      }
    }

    // Generate UUID
    // const generateUUID = () => {
    //   return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    //     const r = Math.random() * 16 | 0
    //     const v = c === 'x' ? r : (r & 0x3 | 0x8)
    //     return v.toString(16)
    //   })
    // }

    // Format price
    const formatPrice = (price) => {
      if (!price && price !== 0) return '0.00'
      return parseFloat(price).toFixed(2)
    }

    // Format date time
    const formatDateTime = (dateString) => {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      if (isNaN(date.getTime())) return dateString

      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    }

    // Format time until next update
    const formatTimeUntilNextUpdate = computed(() => {
      const now = Date.now()
      const diff = nextUpdateTime.value - now

      if (diff <= 0) return '0s'

      const minutes = Math.floor(diff / 60000)
      const seconds = Math.floor((diff % 60000) / 1000)

      return `${minutes}m ${seconds}s`
    })

    // Fetch gold price
    const fetchGoldPrice = async () => {
      loading.value = true
      error.value = null

      try {
        const headers = getHeaders()
        const requestData = { "rateCode": "AUA/CNY" }

        // Using CORS proxy
        const url = 'https://openapi.boc.cn/unlogin/finance/query_market_price';
        console.log("URL", url);
        console.log("headers", headers);

        const response = await axios.post(url, requestData, {headers: headers, timeout: 10000});

        if (response.data && response.data.data) {
          goldPrice.value = response.data.data
          const now = new Date()
          lastUpdated.value = now.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          })
          lastApiCallTime.value = now.toLocaleString()
          nextUpdateTime.value = Date.now() + 3 * 60 * 1000
        } else {
          throw new Error('No data returned from API')
        }
      } catch (err) {
        console.error('Error fetching gold price:', err)

        if (err.code === 'ECONNABORTED') {
          error.value = 'Request timeout. Please try again.'
        } else if (err.response) {
          error.value = `Server error: ${err.response.status} - ${err.response.statusText}`
        } else if (err.request) {
          error.value = 'Network error. Please check your connection.'
        } else {
          error.value = err.message || 'Failed to fetch gold price data'
        }
      } finally {
        loading.value = false
      }
    }

    // Toggle auto-refresh
    const toggleAutoRefresh = () => {
      autoRefresh.value = !autoRefresh.value
    }

    // Start auto-refresh
    const startAutoRefresh = () => {
      if (refreshInterval) {
        clearInterval(refreshInterval)
        refreshInterval = null
      }

      if (autoRefresh.value) {
        refreshInterval = setInterval(() => {
          fetchGoldPrice()
        }, 3 * 60 * 1000) // 3 minutes
      }
    }

    // Watch autoRefresh toggle
    watch(autoRefresh, (newVal) => {
      if (newVal) {
        startAutoRefresh()
      } else if (refreshInterval) {
        clearInterval(refreshInterval)
        refreshInterval = null
      }
    })

    // Initialize
    onMounted(() => {
      fetchGoldPrice()
      startAutoRefresh()

      // Update countdown every second
      setInterval(() => {
        // Force reactivity for computed property
      }, 1000)
    })

    // Cleanup
    onUnmounted(() => {
      if (refreshInterval) {
        clearInterval(refreshInterval)
      }
    })

    return {
      goldPrice,
      loading,
      error,
      lastUpdated,
      lastApiCallTime,
      autoRefresh,
      nextUpdateTime,
      formatPrice,
      formatDateTime,
      formatTimeUntilNextUpdate,
      fetchGoldPrice,
      toggleAutoRefresh
    }
  }
}
</script>

<style scoped>
/* Custom animations */
:deep(.animate-pulse) {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

:deep(.animate-spin) {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>